import * as React from "react"
import { Tabs } from "@chakra-ui/react"
import { UserList } from "../userList"
import { Chat } from "../chat"
import Moderation from "../moderation"
import { Polls } from "../polls"
import { FaPollH } from "react-icons/fa"
import { FaUsers, FaStar } from "react-icons/fa"
import { Icon } from "../../common/icons"
import { BadgesList, HistoryList } from "../badges"
import { ServicesTabList } from "../services-tab-list"
import { ServicesTabPanels } from "../services-tab-panels"
import { TabPanelsListProps } from "../services-tab-panels/services-tab-panels.interface"
import { TabServiceProps } from "../services-tab-list/services-tab-list.interface"
import { getFocus } from "../../../util/getFocus"
import NotificationSound from "../../../assets/sounds/notificationNewMessages/newMessage.mp3"
import ChatCountTabIcon from "../chat/ChatCountTabIcon"
import NewMessageNotification from "../chat/NewMessageNotification"
import { IoChatbubbleSharp, IoGrid, IoSettingsSharp } from "react-icons/io5"
import UserListIcon from "../userList/user-list-icon"
import Videos from "../videos"
import { useTranslation } from "react-i18next"
import { AiFillTag, AiOutlineControl } from "react-icons/ai"
import { Tagged } from "../tagged"
import {
  brandingSelector,
  isShowsGridViewSupportedSelector
} from "../../../recoil/atoms/room"
import { useRecoilValue, useRecoilState, useSetRecoilState } from "recoil"
import { shoppableMNPVideoAtom } from "../../../recoil/atoms/shoppableVideos"
import { FaLink } from "react-icons/fa6"
import { SHOW_DESKTOP_TAB_INDEX } from "../../../constants"
import useMuteAndPauseAllMedia from "../../../hooks/multimedia/useMuteAndPauseAllMedia"
import useConfig from "src/hooks/useConfig"
import {
  shoppableVideoConfigToPanelIcon,
  shoppableVideoConfigToPanelTitleText
} from "../util"
import { GiFairyWand } from "react-icons/gi"
import { InspirationsTabView } from "../inspirations"
import { withErrorBoundary } from "react-error-boundary"
import ComponentFallback from "src/components/common/errors/componentFallback"
import layoutStates from "src/recoil/atoms/layout"
import { serviceTabAtom } from "src/recoil/atoms/service-panele-tab"
import {
  InspirationsButtonIcon,
  inspirationsButtonText
} from "src/shared/components/InspirationsButtonContent"

export interface ModeratorViewProps {
  sidebarOpen: boolean
  setSidebarOpen: (boolean) => void
  servicePanelBackgroundColor: any
  tabButtonStyle: any
  targetTabIndex?: number
  handleTabsChange?: (index: number) => void
  handleClickChatTab: () => void
  liveSellButtonProps: {
    audienceButtonLiveSellIcon: string
    audienceButtonLiveSellText: string
  }
}

export const ModeratorView: React.FC<ModeratorViewProps> = withErrorBoundary(
  React.memo(
    ({
      sidebarOpen,
      servicePanelBackgroundColor,
      tabButtonStyle,
      setSidebarOpen,
      targetTabIndex,
      handleTabsChange,
      handleClickChatTab,
      liveSellButtonProps
    }) => {
      const setChatTab = useSetRecoilState(layoutStates.isChatTab)
      const [activeTabName, setActiveTabName] = useRecoilState(serviceTabAtom)
      const config = useConfig()
      const { t } = useTranslation()
      const handleFocusFirstPoll = React.useCallback(() => {
        getFocus(`title-poll`)
      }, [])
      const roomStyles = useRecoilValue(brandingSelector)
      const [{ enabledShoppableMNP }] = useRecoilState(shoppableMNPVideoAtom)
      const isShowsGridViewSupported = useRecoilValue(
        isShowsGridViewSupportedSelector
      )
      const { muteAndPauseAllMedia, unmuteMedia } = useMuteAndPauseAllMedia()

      // Handle tab change events
      const handleTabsChangeEvent = (index: number) => {
        // unmute the incoming audio which we mute when we open show tab
        if (index !== SHOW_DESKTOP_TAB_INDEX && isShowsGridViewSupported) {
          unmuteMedia()
        }
        handleTabsChange(index)
      }

      const tabSections: {
        panel: TabPanelsListProps
        button: TabServiceProps
      }[] = React.useMemo(() => {
        const sections = []

        if (config.sidepanelUsersEnabled === true) {
          sections.push({
            panel: {
              id: 0,
              tabPanel: <UserList />,
              styles: { height: "100%", padding: 0 }
            },
            button: {
              id: 0,
              icon: <Icon src={FaUsers} boxSize="1.2em" />,
              render: props => <UserListIcon {...props} />,
              text: t("Users")
            }
          })
        }

        if (config.sidepanelChatEnabled === true) {
          sections.push({
            panel: {
              id: 1,
              tabPanel: (
                <Chat
                  servicePanelBackgroundColor={servicePanelBackgroundColor}
                />
              ),
              styles: {
                display: "flex",
                flexDirection: "column",
                height: "100%",
                padding: 0
              }
            },
            button: {
              id: 1,
              icon: (
                <Icon
                  src={IoChatbubbleSharp}
                  boxSize="1.2em"
                  color="transparent"
                  strokeWidth="3.5rem"
                  stroke={"inherit"}
                />
              ),
              text: t("Chat"),
              render: props => <ChatCountTabIcon {...props} />,
              callback: handleClickChatTab
            }
          })
        }

        if (config.sidepanelLinksEnabled === true) {
          sections.push({
            panel: {
              id: 2,
              tabPanel: <BadgesList />,
              styles: { height: "calc(100% - 51px)", padding: 0 }
            },
            button: {
              id: 2,
              icon: (
                <Icon src={FaLink} boxSize="1.2em" /* strokeWidth="3px" */ />
              ),
              text: "Links"
            }
          })
        }

        if (config.sidepanelFeaturedEnabled === true) {
          sections.push({
            panel: {
              id: 3,
              tabPanel: <HistoryList />,
              styles: { height: "calc(100% - 51px)", padding: 0 }
            },
            button: {
              id: 3,
              icon: <Icon src={FaStar} boxSize="1.2em" />,
              text: t("Featured")
            }
          })
        }

        if (config.sidepanelVideosEnabled === true) {
          sections.push({
            panel: {
              id: 4,
              tabPanel: <Videos />,
              styles: { height: "calc(100% - 51px)", padding: 0 }
            },
            button: {
              id: 4,
              icon: (
                <Icon
                  src={shoppableVideoConfigToPanelIcon(config.shoppableVideos)}
                  boxSize="1.2em"
                />
              ),
              text: t(
                shoppableVideoConfigToPanelTitleText(config.shoppableVideos)
              ),
              customStyles: enabledShoppableMNP
                ? {
                    background: `${roomStyles?.secondaryAccessButtonSelectedColor} !important`,
                    color: `${roomStyles?.secondaryAccessButtonSelectedTextColor} !important`
                  }
                : undefined
            }
          })
        }

        if (config.sidepanelTaggedEnabled === true) {
          sections.push({
            panel: {
              id: 5,
              tabPanel: <Tagged />,
              styles: { height: "calc(100% - 51px)", padding: 0 }
            },
            button: {
              id: 5,
              icon: <Icon src={AiFillTag} boxSize="1.2em" />,
              text: t("Tagged")
            }
          })
        }

        if (config.sidepanelPollsEnabled === true) {
          sections.push({
            panel: {
              id: 6,
              tabPanel: <Polls />,
              styles: { height: "100%", padding: 0 }
            },
            button: {
              id: 6,
              icon: <Icon src={FaPollH} boxSize="1.2em" />,
              text: t("Polls"),
              callback: handleFocusFirstPoll
            }
          })
        }

        if (config.sidepanelControlsEnabled === true) {
          sections.push({
            panel: {
              id: 9,
              tabPanel: <Moderation />,
              styles: { height: "100%", padding: 0 /*  overflow: 'hidden' */ }
            },
            button: {
              id: 9,
              icon: <Icon src={AiOutlineControl} boxSize="1.2em" />,
              text: t("Controls")
            }
          })
        }

        if (config.sidepanelInspirationsEnabled === true) {
          sections.push({
            panel: {
              id: 11,
              tabPanel: (
                <InspirationsTabView
                  openChat={() => {
                    handleTabsChange(0)
                    setChatTab(true)
                    setActiveTabName("chat")

                    handleClickChatTab()
                  }}
                  isMobile={false}
                />
              ),
              styles: { height: "calc(100% - 51px)", padding: 0 }
            },
            button: {
              id: 11,
              icon: <InspirationsButtonIcon boxSize="1.2em" />,
              text: inspirationsButtonText(config)
            }
          })
        }

        if (isShowsGridViewSupported) {
          sections.splice(SHOW_DESKTOP_TAB_INDEX, 0, {
            panel: null,
            button: {
              id: SHOW_DESKTOP_TAB_INDEX,
              icon: <Icon src={IoGrid} boxSize="1.2em" />,
              text: t("Shows"),
              callback: muteAndPauseAllMedia
            }
          })
        }

        return sections
      }, [
        servicePanelBackgroundColor,
        isShowsGridViewSupported,
        servicePanelBackgroundColor,
        isShowsGridViewSupported,
        liveSellButtonProps?.audienceButtonLiveSellText,
        liveSellButtonProps?.audienceButtonLiveSellIcon,
        enabledShoppableMNP,
        isShowsGridViewSupported,
        config
      ])

      return (
        <>
          <NewMessageNotification targetTabIndex={targetTabIndex} />
          <Tabs
            isLazy
            lazyBehavior="keepMounted"
            position="relative"
            variant="line"
            orientation="vertical"
            height="100%"
            index={targetTabIndex}
            onChange={handleTabsChangeEvent}
          >
            <div>
              <audio
                id="notification-new-messages-player"
                src={NotificationSound}
                defaultValue={0.1}
              />
            </div>
            <ServicesTabPanels
              sidebarOpen={sidebarOpen}
              servicePanelBackgroundColor={servicePanelBackgroundColor}
              tabPanelsList={tabSections?.map(section => section.panel) ?? []}
            />
            <ServicesTabList
              setSidebarOpen={setSidebarOpen}
              tabButtonStyle={tabButtonStyle}
              tabList={tabSections?.map(section => section.button) ?? []}
              sidebarOpen={sidebarOpen}
              showCollapseButton={true}
            />
          </Tabs>
        </>
      )
    }
  ),
  { fallback: <ComponentFallback /> }
)
